import React, { useEffect, useState } from 'react';
import { Table, TableHead, TableBody, TableRow, TableCell, Typography } from '@mui/material';
import DraggableRow from './DraggableRow';
import { downloadExcel } from '../../../utils/excelFileHelper';
import { SCUIButton } from '../../atoms';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useTranslation } from 'react-i18next';

interface SCUIDraggableCustomTableProps {
    columns: any;
    rows: Array<{ [key: string]: any }>;
    excelDownloadButton?: boolean;
    excelDownloadButtonName?: string;
    onTableSortUpdate: (rows: any) => void;
}

const SCUIDraggableCustomTable: React.FC<SCUIDraggableCustomTableProps> = ({
    columns,
    rows,
    excelDownloadButton,
    excelDownloadButtonName,
    onTableSortUpdate,
}) => {
    const [tableRows, setTableRows] = useState<any>(rows);
    const { t } = useTranslation();
    useEffect(() => {
        setTableRows(rows);
    }, [rows]);

    const moveRow = (fromIndex: number, toIndex: number) => {
        if (fromIndex === toIndex) return;

        const updatedRows = [...tableRows];
        const [movedRow] = updatedRows.splice(fromIndex, 1);
        updatedRows.splice(toIndex, 0, movedRow);
        setTableRows(updatedRows);
    };

    const dropRow = () => {
        onTableSortUpdate(tableRows);
    };

    const downloadExcelHandler = () => {
        downloadExcel({
            excelDownloadData: tableRows,
            excelDownloadFolderName: 'table_export',
        });
    };

    return (
        <div>
            {excelDownloadButton && (
                <SCUIButton
                    type={'primary'}
                    sizeType={'fixed'}
                    size={'s'}
                    text={excelDownloadButtonName || t('general.exportToExcel')}
                    showStartIcon
                    startIcon={<FileDownloadIcon style={{ color: 'white' }} />}
                    onClick={downloadExcelHandler}
                />
            )}
            <Table sx={{ tableLayout: 'auto', width: '100%' }}>
                <TableHead>
                    <TableRow
                        sx={{
                            backgroundColor: 'rgba(0,0,0,0.03)',
                            borderBottom: '2px solid',
                            borderColor: 'divider',
                        }}
                    >
                        {columns
                            .filter((col: any) => !col.isHide)
                            .map((col: any, index: any) => (
                                <TableCell
                                    key={index}
                                    align={col.align || 'left'}
                                    sx={{ py: 1.5, px: 2, borderBottom: 'none' }}
                                >
                                    {col.icon && <col.icon />}
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="text.secondary"
                                        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                                    >
                                        {col.headerName}
                                    </Typography>
                                </TableCell>
                            ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableRows.map((row: any, index: any) => (
                        <DraggableRow
                            key={row.id || index}
                            id={row.id || index}
                            index={index}
                            moveRow={moveRow}
                            dropRow={dropRow}
                        >
                            {columns.map((col: any, colIndex: any) => (
                                <TableCell key={colIndex} align={col.align || 'left'} sx={{ py: 1.25, px: 2 }}>
                                    {col.renderCell ? col.renderCell({ row }) : row[col.field]}
                                </TableCell>
                            ))}
                        </DraggableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

export default SCUIDraggableCustomTable;
